import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from './contexts/LanguageContext';
import {
  Award, BookOpen, Bot, FileText, LogIn, LogOut, Mail, Menu, MessageCircle, Play, Plus, RefreshCw, Send, Users, Zap
} from 'lucide-react';
import LanguageSelector from './components/LanguageSelector';
import { getTierMeta } from './utils/tierMeta';
import { pickQuickplayBotTypes } from './utils/quickplayBots';

function Lobby({ socket, username, onCreateRoom, onJoinRoom, onLogout = () => {}, tiers = null, onStartChallenge = () => {} }) {
  const { t, version } = useLanguage();
  const [rooms, setRooms] = useState([]);
  const [ongoingGames, setOngoingGames] = useState(0);
  const [ongoingPlayers, setOngoingPlayers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [onlineUserCount, setOnlineUserCount] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showBotInfo, setShowBotInfo] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactStatus, setContactStatus] = useState(null);
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const chatMessagesRef = useRef(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);
  const menuRef = useRef(null);
  const chatRef = useRef(null);

  const getApiBase = () => {
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:3001';
    }
    if (
      window.location.hostname.startsWith('192.168.') ||
      window.location.hostname.startsWith('10.')
    ) {
      return `http://${window.location.hostname}:3001`;
    }
    return '';
  };

  const API_BASE = getApiBase();

  const scrollToBottom = () => {
    // Scroll only the message list; scrollIntoView would also scroll the page when the chat is stacked on phones.
    const list = chatMessagesRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  };

  const [isGuestUser, setIsGuestUser] = useState(false);
  const tierLevel = tiers?.tier || 0;
  const tierInfo = getTierMeta(tierLevel);
  const tierLabel = t(tierInfo.labelKey) || tierInfo.defaultLabel;

  // Each mini-game is a challenge reward (MINI-01); nginx checks the same
  // tier on the server before serving the page.
  const canAccessJewels = !isGuestUser && tierLevel >= 1;
  const canAccessMinesweeper = !isGuestUser && tierLevel >= 2;
  const canAccessShadow = !isGuestUser && tierLevel >= 3;

  // Phones show no tooltips, so a tap on a locked game says why it is locked.
  const [eggNotice, setEggNotice] = useState(null);
  // nginx sends a player without the tier back here (/?minigame=locked).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('minigame') !== 'locked') return;
    setEggNotice(t('rules.eggLocked') || 'Complete the challenge tier for this game to unlock it.');
    params.delete('minigame');
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
  }, [t]);

  useEffect(() => {
    if (!eggNotice) return undefined;
    const timer = setTimeout(() => setEggNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [eggNotice]);

  const getEggLockMessage = (requiredTier) => {
    if (isGuestUser) {
      return t('rules.eggGuest') || 'Log in with an account to access this easter egg.';
    }
    return (t('rules.eggTier') || 'Complete Challenge {{tier}} to unlock this easter egg.').replace('{{tier}}', requiredTier);
  };

  const renderTierBadge = (tier, size = 'md') => {
    const meta = getTierMeta(tier);
    const label = t(meta.labelKey) || meta.defaultLabel;
    const sizeClass = size === 'small' ? 'tier-badge--small' : '';
    return (
      <span
        className={`tier-badge tier-badge-${meta.level} ${sizeClass}`.trim()}
        title={label}
      >
        {meta.icon}
      </span>
    );
  };
  const localizedRulesList = t('rules.list');
  const rulesList = (Array.isArray(localizedRulesList) ? localizedRulesList : [localizedRulesList]).filter(Boolean);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('user');
      const parsed = stored ? JSON.parse(stored) : null;
      setIsGuestUser(Boolean(parsed?.isGuest));
      if (parsed?.username) {
        setContactForm(prev => ({ ...prev, name: prev.name || parsed.username }));
      }
      if (parsed?.email) {
        setContactForm(prev => ({ ...prev, email: prev.email || parsed.email }));
      }
    } catch (err) {
      setIsGuestUser(false);
    }
  }, [username]);

  const createEasterEggHandler = (eggKey, url) => async (event) => {
    event.preventDefault();
    const payload = JSON.stringify({ eggKey });
    const endpoint = `${API_BASE}/api/easter-eggs/click`;
    const openLink = () => window.open(url, '_blank', 'noopener,noreferrer');

    try {
      if (navigator?.sendBeacon) {
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(endpoint, blob);
        openLink();
        return;
      }

      await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true
      });
      openLink();
    } catch (error) {
      console.error('Failed to record easter egg click', error);
      openLink();
    }
  };

  const openContactModal = () => {
    setShowContactModal(true);
    setContactStatus(null);
    if ((!contactForm.name || !contactForm.email) && typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('user');
        const parsed = stored ? JSON.parse(stored) : null;
        if (parsed?.username || parsed?.email) {
          setContactForm(prev => ({
            ...prev,
            name: prev.name || parsed?.username || '',
            email: prev.email || parsed?.email || ''
          }));
        }
      } catch (err) {
        // ignore
      }
    }
  };

  const handleContactChange = (field) => (event) => {
    const value = event.target.value;
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setContactStatus(null);

    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      setContactStatus({ type: 'error', text: 'Please fill in all fields.' });
      return;
    }

    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      setContactStatus({ type: 'error', text: 'You need to be logged in to send a message.' });
      return;
    }

    setIsSubmittingContact(true);
    try {
      const response = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(contactForm)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error?.msg || 'Failed to send your message.');
      }

      setContactStatus({ type: 'success', text: 'Message sent! Thanks for reaching out.' });
      setContactForm(prev => ({ ...prev, message: '' }));
    } catch (error) {
      setContactStatus({ type: 'error', text: error.message || 'Failed to send your message.' });
    } finally {
      setIsSubmittingContact(false);
    }
  };

  useEffect(() => {
    // Only auto-scroll if explicitly enabled (after user interaction)
    if (shouldAutoScroll) {
      scrollToBottom();
    }
  }, [chatMessages, shouldAutoScroll]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Request room list when component mounts
    socket.emit('getRooms');

    // Listen for room list updates (real-time updates from server)
    socket.on('roomsList', (data) => {
      console.log('🏠 LOBBY: Received real-time roomsList update:', data);
      
      // Handle both old array format and new object format
      if (Array.isArray(data.rooms)) {
        console.log('🏠 LOBBY: Setting rooms from data.rooms:', data.rooms.length);
        setRooms(data.rooms);
      } else if (Array.isArray(data)) {
        // Old format - data is directly the rooms array
        console.log('🏠 LOBBY: Setting rooms from data array:', data.length);
        setRooms(data);
      } else {
        console.warn('⚠️ LOBBY: Unexpected roomsList data format:', data);
        setRooms([]);
      }
      setOngoingGames(data.ongoingGames || 0);
      setOngoingPlayers(data.ongoingPlayers || 0);
      setLoading(false);
    });

    // Listen for errors
    socket.on('error', (data) => {
      alert('Error: ' + data.message);
      setLoading(false);
    });

    // Listen for lobby chat messages
    socket.on('lobbyChatMessage', (data) => {
      setChatMessages(prev => [...prev, { ...data, tier: data?.tier ?? 0 }]);
    });

    // Listen for online user count updates
    socket.on('onlineUserCount', (data) => {
      console.log('👥 LOBBY: Received user count update:', data.count);
      setOnlineUserCount(data.count);
    });

    return () => {
      socket.off('roomsList');
      socket.off('error');
      socket.off('lobbyChatMessage');
      socket.off('onlineUserCount');
    };
  }, [socket]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading) {
        console.log('🔄 LOBBY: Fallback polling for room list');
        socket.emit('getRooms');
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [socket, loading]);

  const handleCreateRoom = () => {
    setLoading(true);
    console.log('Creating room with socket.id:', socket.id);
    socket.emit('createRoom', { username });
    
    socket.once('roomCreated', (data) => {
      console.log('Room created:', data);
      onCreateRoom(data.roomId, data.roomInfo);
    });
  };

  const handleJoinRoom = (roomId) => {
    setLoading(true);
    socket.emit('joinRoom', { roomId, username });
    onJoinRoom(roomId);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (chatInput.trim()) {
      socket.emit('lobbyChatMessage', { message: chatInput, tier: tierLevel });
      setChatInput('');
      setShouldAutoScroll(true); // Enable auto-scroll when user sends a message
    }
  };

  // Generate consistent color for usernames
  const getUsernameColor = (username) => {
    const colors = ['#66BB6A', '#2196F3', '#AB47BC', '#FF7043', '#00E676', '#FFCA28', '#EF5350'];
    let hash = 0;
    for (let i = 0; i < username.length; i++) {
      hash = username.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Convert room ID to display name (e.g., "lightning-falcon-ke9k" -> "Lightning Falcon")
  const getRoomDisplayName = (roomId) => {
    // Handle old format room-1234567890
    if (roomId.startsWith('room-')) {
      return roomId;
    }
    
    // Handle new format: "lightning-falcon-ke9k" -> "Lightning Falcon"
    // Split by dash and take only first two parts (ignore hash suffix)
    const parts = roomId.split('-');
    if (parts.length >= 2) {
      const adjective = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
      const animal = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
      return `${adjective} ${animal}`;
    }
    
    return roomId; // Fallback to original ID
  };

  const handleQuickPlay = () => {
    setLoading(true);
    console.log('Starting quickplay with socket.id:', socket.id);
    socket.emit('createRoom', { username });
    
    socket.once('roomCreated', (data) => {
      console.log('Quickplay room created:', data);
      const roomId = data.roomId;
      
      const botTypes = pickQuickplayBotTypes();
      console.log('🎲 Quickplay bot types selected:', botTypes.join(', '));
      
      socket.emit('addBot', { roomId, slotIndex: 1, botType: botTypes[0] });
      socket.emit('addBot', { roomId, slotIndex: 2, botType: botTypes[1] });
      socket.emit('addBot', { roomId, slotIndex: 3, botType: botTypes[2] });
      
      // Start game shortly after bots join (host defaults to ready)
      setTimeout(() => {
        socket.emit('startGame', { roomId });
      }, 1000);
      
      onCreateRoom(data.roomId, data.roomInfo);
    });
  };

  const getInitial = (name) => (name || '?').charAt(0).toUpperCase();
  const roomSuits = ['♠', '♥', '♣', '♦'];
  const tileCards = [
    { suit: '♣', red: false, x: -34, r: -20 },
    { suit: '♦', red: true, x: -6, r: -4 },
    { suit: '♥', red: true, x: 22, r: 12 },
    { suit: '♠', red: false, x: 48, r: 28 }
  ];

  return (
    <div className="lobby-container">
      <header className="lobby-topbar">
        <div className="ct-brand">
          <span className="ct-brand-mark" aria-hidden="true">K</span>
          <span className="ct-brand-name">Kwajongen<small>Genk</small></span>
        </div>
        <span className="lobby-topbar-title">{t('lobby.title')}</span>
        <span className="lobby-topbar-spacer" />
        <span className="ct-pill lobby-topbar-online">
          <span className="ct-dot" />
          {t('lobby.chat.online', { count: onlineUserCount })}
        </span>
        <button
          type="button"
          className="ct-icon-btn lobby-chat-jump"
          onClick={() => chatRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          aria-label={t('lobby.chat.short')}
        >
          <MessageCircle aria-hidden="true" />
        </button>
        <div className="lobby-user">
          <span className="ct-avatar" aria-hidden="true">{getInitial(username)}</span>
          <span className="lobby-user-text">
            <b>{username}</b>
            <small>{isGuestUser ? t('lobby.guestAccount') : tierLabel}</small>
          </span>
        </div>
        <div className="lobby-menu" ref={menuRef}>
          <button
            type="button"
            className="ct-icon-btn"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            aria-label={t('lobby.menu')}
            aria-expanded={isMenuOpen}
          >
            <Menu aria-hidden="true" />
          </button>
          {isMenuOpen && (
            <div className="lobby-menu-panel">
              <button
                type="button"
                className="lobby-menu-item"
                onClick={() => { setShowRules(true); setIsMenuOpen(false); }}
              >
                <BookOpen aria-hidden="true" />
                {t('rules.button') || 'How to play'}
              </button>
              <button
                type="button"
                className="lobby-menu-item"
                onClick={() => { setShowBotInfo(true); setIsMenuOpen(false); }}
              >
                <Bot aria-hidden="true" />
                {t('bots.button') || 'Bot AI Explained'}
              </button>
              <LanguageSelector className="language-selector--menu" />
              <button
                type="button"
                className="lobby-menu-item lobby-menu-item--logout"
                onClick={onLogout}
              >
                <LogOut aria-hidden="true" />
                {t('common.logout') || 'Logout'}
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="lobby-layout">
        <main className="lobby-main">
          <div className="lobby-hello">
            <div>
              <h1 className="tier-badge-wrapper">
                {t('lobby.welcome', { username })}
                {renderTierBadge(tierLevel)}
              </h1>
              <p>{t('lobby.welcomeSub')}</p>
            </div>
            <div className="challenge-tier-banner">
              <Award aria-hidden="true" />
              {t('lobby.currentTier') || 'Current Tier'}: <strong>{tierLabel}</strong>
            </div>
          </div>

          <div className="lobby-tiles">
            <button
              type="button"
              onClick={handleQuickPlay}
              disabled={loading}
              className="lobby-tile lobby-tile--play quickplay-btn"
            >
              <span className="lobby-tile-cards" aria-hidden="true">
                {tileCards.map((card) => (
                  <span
                    key={card.suit}
                    className={`lobby-tile-card ${card.red ? 'lobby-tile-card--red' : ''}`.trim()}
                    style={{ transform: `rotate(${card.r}deg) translateX(${card.x}px)` }}
                  >
                    J<br />{card.suit}
                  </span>
                ))}
              </span>
              <span className="lobby-tile-icon" aria-hidden="true"><Zap /></span>
              <span className="lobby-tile-title">{t('lobby.quickPlayTitle')}</span>
              <span className="lobby-tile-desc">{t('lobby.quickPlayDesc')}</span>
              <span className="lobby-tile-cta"><Play aria-hidden="true" />{t('lobby.quickPlay')}</span>
            </button>
            <button
              type="button"
              onClick={handleCreateRoom}
              disabled={loading}
              className="lobby-tile lobby-tile--create create-room-btn"
            >
              <span className="lobby-tile-icon" aria-hidden="true"><Plus /></span>
              <span className="lobby-tile-title">{t('lobby.createRoom')}</span>
              <span className="lobby-tile-desc">{t('lobby.createRoomDesc')}</span>
              <span className="lobby-tile-cta"><Users aria-hidden="true" />{t('lobby.createRoomCta')}</span>
            </button>
          </div>

          <section className="lobby-section lobby-challenges">
            <div className="lobby-section-head">
              <h2>{t('lobby.challengesTitle')}</h2>
              <span className="lobby-section-meta">{t('lobby.challengesHint')}</span>
            </div>
            <div className="challenge-buttons">
              {[1, 2, 3].map(challenge => {
                const earnedTier = tiers?.tier || 0;
                const unlocked = challenge === 1 ? true : earnedTier >= (challenge - 1);
                const details = {
                  1: {
                    label: 'Copper Trials',
                    description: 'Beat the smart squad (you + veteran vs 2 smart bots).',
                    reward: 'Earn the Copper badge + Jewels access.'
                  },
                  2: {
                    label: 'Silver Gauntlet',
                    description: 'Beat the veteran squad (you + veteran vs 2 veterans).',
                    reward: 'Earn the Silver badge + Minesweeper access.'
                  },
                  3: {
                    label: 'Golden Crown',
                    description: 'Beat the strongest bots (you + veteran vs 2 masters).',
                    reward: 'Earn the Gold crown + Shadowruler access.'
                  }
                }[challenge];
                const tooltip = unlocked
                  ? `${details?.description || ''} ${details?.reward || ''}`.trim()
                  : `Complete Challenge ${challenge - 1} to unlock this tier.`;
                const state = !unlocked ? 'locked' : earnedTier >= challenge ? 'completed' : 'playable';
                const stateLabel = {
                  locked: t('lobby.tierLocked'),
                  completed: t('lobby.tierCompleted'),
                  playable: t('lobby.tierPlayable')
                }[state];
                const statusIcon = !unlocked ? '🔒 ' : earnedTier >= challenge ? '✅ ' : '';
                return (
                  <button
                    type="button"
                    key={challenge}
                    className={`challenge-card challenge-card--tier${challenge} challenge-card--${state}`}
                    style={{ opacity: unlocked ? 1 : 0.55 }}
                    disabled={!unlocked || loading}
                    onClick={() => unlocked && onStartChallenge(challenge)}
                    title={tooltip}
                  >
                    <span className="challenge-card-tier">{`${statusIcon}Tier ${challenge}`}</span>
                    <span className="challenge-card-label">{details?.label}</span>
                    <span className="challenge-card-title">{t(getTierMeta(challenge).labelKey)}</span>
                    <span className="challenge-card-state">{stateLabel}</span>
                    <span className="challenge-medal" aria-hidden="true">{'I'.repeat(challenge)}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="lobby-section rooms-list">
            <div className="lobby-section-head">
              <h2>
                {t('lobby.availableRooms')}
                <span className="lobby-count">{rooms.length}</span>
              </h2>
              <span className="lobby-section-meta"><RefreshCw aria-hidden="true" />{t('lobby.roomsLive')}</span>
            </div>
            <div className="lobby-rooms">
              {rooms.length === 0 ? (
                <div className="no-rooms">
                  <span className="no-rooms-stack" aria-hidden="true"><i /><i /><i /></span>
                  <h3>{t('lobby.emptyTitle')}</h3>
                  {ongoingGames > 0 ? (
                    <>
                      <p>{t('lobby.gamesInProgress', {
                        count: ongoingGames,
                        plural: ongoingGames > 1 ? 's' : '',
                        players: ongoingPlayers,
                        playerPlural: ongoingPlayers > 1 ? 's' : ''
                      })}</p>
                      <p>{t('lobby.createNewRoom')}</p>
                    </>
                  ) : (
                    <p>{t('lobby.noRooms')}</p>
                  )}
                  <button type="button" className="ct-btn ct-btn--gold ct-btn--inline" onClick={handleCreateRoom} disabled={loading}>
                    <Plus aria-hidden="true" />
                    {t('lobby.createRoom')}
                  </button>
                </div>
              ) : (
                rooms.map((room, index) => {
                  const isFull = room.playerCount >= room.maxPlayers;
                  return (
                    <div key={room.roomId} className="room-card">
                      <span className="room-card-icon" aria-hidden="true">{roomSuits[index % roomSuits.length]}</span>
                      <div className="room-info">
                        <h3>{getRoomDisplayName(room.roomId)}</h3>
                        <p>
                          {t('lobby.host')}: <b>{room.hostUsername}</b>
                          <span className="room-info-count"> · {room.playerCount}/{room.maxPlayers}</span>
                          {room.status && <span className="room-card-status"> · {room.status}</span>}
                        </p>
                      </div>
                      <span className="room-seats" aria-hidden="true">
                        {Array.from({ length: room.maxPlayers || 0 }, (_, seat) => (
                          <i key={seat} className={seat < room.playerCount ? 'is-taken' : ''} />
                        ))}
                      </span>
                      <span className="room-count"><b>{room.playerCount}</b>/{room.maxPlayers} {t('lobby.players')}</span>
                      <button
                        type="button"
                        onClick={() => handleJoinRoom(room.roomId)}
                        disabled={loading || isFull}
                        className="join-btn"
                      >
                        {isFull ? t('lobby.full') : <><LogIn aria-hidden="true" />{t('lobby.join')}</>}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </main>

        <aside className="lobby-chat" ref={chatRef}>
          <div className="lobby-chat-head">
            <h2><MessageCircle aria-hidden="true" />{t('lobby.chat.title')}</h2>
            <span className="lobby-chat-online">
              <span className="ct-dot" />
              {t('lobby.chat.online', { count: onlineUserCount })}
            </span>
          </div>
          <div className="chat-messages" ref={chatMessagesRef}>
            {chatMessages.length === 0 ? (
              <p className="chat-empty-state">{t('lobby.chat.noMessages')}</p>
            ) : (
              chatMessages.map((msg, index) => {
                const time = new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                if (msg.type === 'system') {
                  return (
                    <div key={index} className="chat-system">
                      <Zap aria-hidden="true" />
                      <span>{msg.message}</span>
                      <time>{time}</time>
                    </div>
                  );
                }
                const color = getUsernameColor(msg.username);
                return (
                  <div key={index} className={`chat-message ${msg.username === username ? 'chat-message--me' : ''}`.trim()}>
                    <span className="ct-avatar ct-avatar--round" style={{ background: color }} aria-hidden="true">
                      {getInitial(msg.username)}
                    </span>
                    <div className="chat-message-body">
                      <div className="chat-message-meta">
                        {renderTierBadge(msg?.tier ?? 0, 'small')}
                        <b style={{ color }}>{msg.username}</b>
                        <time>{time}</time>
                      </div>
                      <p className="chat-message-text">{msg.message}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <form onSubmit={handleSendMessage} className="chat-input-container">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder={t('lobby.chat.placeholder')}
              className="chat-input"
            />
            <button type="submit" className="chat-send-btn">
              <Send aria-hidden="true" />
              {t('lobby.chat.send')}
            </button>
          </form>
        </aside>

        <footer className="lobby-easter-egg-footer">
          <span className="lobby-footer-brand">Kwajongen Genk</span>
          <div className="lobby-footer-actions">
            <button
              type="button"
              className="lobby-legal-button"
              onClick={() => window.location.assign('/legal')}
            >
              <FileText aria-hidden="true" />
              {t('lobby.legal')}
            </button>
            <button
              type="button"
              className="lobby-legal-button"
              onClick={openContactModal}
            >
              <Mail aria-hidden="true" />
              {t('lobby.contact')}
            </button>
          </div>
          <div className="lobby-easter-egg-links">
            <span className="lobby-easter-egg-label">{t('lobby.miniGames')}:</span>
            <a
              className={`lobby-easter-egg-link ${canAccessJewels ? '' : 'guest-locked'}`.trim()}
              href={canAccessJewels ? '/jewels/' : '#tier-lock'}
              target={canAccessJewels ? '_blank' : undefined}
              rel={canAccessJewels ? 'noopener noreferrer' : undefined}
              title={canAccessJewels ? '💎 Jewels game' : getEggLockMessage(1)}
              onClick={canAccessJewels ? createEasterEggHandler('jewels', '/jewels/') : (e) => {
                e.preventDefault();
                setEggNotice(getEggLockMessage(1));
              }}
            >
              <span aria-hidden="true">💎</span>
              Jewels
            </a>
            <a
              className={`lobby-easter-egg-link ${canAccessMinesweeper ? '' : 'guest-locked'}`.trim()}
              href={canAccessMinesweeper ? '/minesweeper/' : '#tier-lock'}
              target={canAccessMinesweeper ? '_blank' : undefined}
              rel={canAccessMinesweeper ? 'noopener noreferrer' : undefined}
              title={canAccessMinesweeper ? '💣 Minesweeper game' : getEggLockMessage(2)}
              onClick={canAccessMinesweeper ? createEasterEggHandler('minesweeper', '/minesweeper/') : (e) => {
                e.preventDefault();
                setEggNotice(getEggLockMessage(2));
              }}
            >
              <span aria-hidden="true">💣</span>
              Minesweeper
            </a>
            <a
              className={`lobby-easter-egg-link ${canAccessShadow ? '' : 'guest-locked'}`.trim()}
              href={canAccessShadow ? '/shadowruler/' : '#tier-lock'}
              target={canAccessShadow ? '_blank' : undefined}
              rel={canAccessShadow ? 'noopener noreferrer' : undefined}
              title={canAccessShadow ? '🤫 Secret Shadowruler game' : getEggLockMessage(3)}
              onClick={canAccessShadow ? createEasterEggHandler('shadowruler', '/shadowruler/') : (e) => {
                e.preventDefault();
                setEggNotice(getEggLockMessage(3));
              }}
            >
              <span aria-hidden="true">🏰</span>
              Shadow Ruler
            </a>
          </div>
          <span className="lobby-easter-egg-version">v{version}</span>
        </footer>
      </div>
      {eggNotice && (
        <div className="lobby-easter-egg-notice" role="status">{eggNotice}</div>
      )}


      {showRules && (
        <div className="rules-modal-overlay" onClick={() => setShowRules(false)}>
          <div className="rules-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('rules.title') || 'How to play'}</h3>
            <p>{t('rules.intro')}</p>
            <p>{t('rules.summary')}</p>
            <ul>
              {rulesList.map((entry, idx) => {
                if (entry && typeof entry === 'object' && Array.isArray(entry.items)) {
                  return (
                    <li key={`${entry.title || idx}-section`}>
                      <strong>{entry.title}</strong>
                      <ul className="rules-sublist">
                        {entry.items.map((bullet, bulletIdx) => (
                          <li key={`${entry.title || idx}-bullet-${bulletIdx}`}>{bullet}</li>
                        ))}
                      </ul>
                    </li>
                  );
                }
                return <li key={`rule-${idx}`}>{entry}</li>;
              })}
            </ul>
            <button
              type="button"
              className="rules-close-btn"
              onClick={() => setShowRules(false)}
            >
              {t('rules.close') || 'Close'}
            </button>
          </div>
        </div>
      )}

      {showBotInfo && (
        <div className="rules-modal-overlay" onClick={() => setShowBotInfo(false)}>
          <div className="rules-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('bots.title') || 'Bot AI & Difficulty Explained'}</h3>

            <p>{t('bots.intro') || 'The 6 available bots are:'}</p>

            <div style={{ marginBottom: 20 }}>
              <h4 style={{ color: '#3b82f6', marginBottom: 10 }}>
                {t('bots.ruleBasedTitle') || '3 Rule-Based Bots (Heuristics)'}
              </h4>
              <ul>
                <li>
                  <strong>{t('bots.beginner.name') || 'Beginner'}:</strong>{' '}
                  {t('bots.beginner.desc') || 'Plays defensively, is not very competitive'}
                </li>
                <li>
                  <strong>{t('bots.aggro.name') || 'Aggro'}:</strong>{' '}
                  {t('bots.aggro.desc') || 'Plays aggressively, will trump immediately'}
                </li>
                <li>
                  <strong>{t('bots.smart.name') || 'Smart'}:</strong>{' '}
                  {t('bots.smart.desc') || 'More well-rounded approach, blending defensive and opportunistic plays'}
                </li>
              </ul>
            </div>

            <div style={{ marginBottom: 20 }}>
              <h4 style={{ color: '#8b5cf6', marginBottom: 10 }}>
                {t('bots.mctsTitle') || '2 "Thinking" Bots (IS-MCTS)'}
              </h4>
              <p style={{ fontSize: 14, marginBottom: 10 }}>
                {t('bots.mctsExplain') || 'These bots use Monte Carlo simulations. They keep track of which cards were played and which suits players could not follow, then reason about different "futures" and pick the card most likely to win.'}
              </p>
              <ul>
                <li>
                  <strong>{t('bots.veteran.name') || 'Veteran'}:</strong>{' '}
                  {t('bots.veteran.desc') || 'Uses an aggressive thinking approach, prioritising short term wins'}
                </li>
                <li>
                  <strong>{t('bots.master.name') || 'Master'}:</strong>{' '}
                  {t('bots.master.desc') || 'Samples the widest range of hands the other players could be holding and picks the card that comes out best across all of them. The strongest bot at the table.'}
                </li>
              </ul>
            </div>

            <div style={{ marginBottom: 20 }}>
              <h4 style={{ color: '#e64a19', marginBottom: 10 }}>
                {t('bots.jevTitle') || '1 Language-Model Bot'}
              </h4>
              <p style={{ fontSize: 14, marginBottom: 10 }}>
                {t('bots.jevExplain') || 'This bot is handed the state of the hand in words and asked which of the legal cards to play, and whether to accept the trump. It reasons about the position rather than searching through it, so it plays a recognisably human game: usually sensible, sometimes inspired, sometimes loose.'}
              </p>
              <ul>
                <li>
                  <strong>{t('bots.jev.name') || 'JEV'}:</strong>{' '}
                  {t('bots.jev.desc') || 'Reasons about the hand in plain language instead of calculating it. Plays like a person rather than a solver.'}
                </li>
              </ul>
            </div>

            <p style={{ fontSize: 14, marginTop: 20, padding: 12, background: 'rgba(59,130,246,0.1)', borderRadius: 8 }}>
              {t('bots.honesty') || 'The bots do not cheat, they can only see their own cards and the game board state.'}
            </p>

            <p style={{ fontSize: 13, marginTop: 16, fontStyle: 'italic', color: 'rgba(15,23,42,0.6)' }}>
              {t('bots.development') || 'These IS-MCTS bots were selected as the 3 highest performing bots from tournaments against the rule-based bots and other bots created through RL (Reinforcement Learning) and GA (Genetic Algorithms). The bots are not yet as strong as they could be but improving them further requires a sophisticated approach and lots of time testing and tuning.'}
            </p>

            <p style={{ fontSize: 13, marginTop: 12, fontStyle: 'italic', color: 'rgba(15,23,42,0.6)' }}>
              {t('bots.feedback') || 'Feel free to provide your experience about the bots, there is a feedback button at the bottom of the page. Let me know if you want me to pursue trying to create a superhuman kwajongen bot.'}
            </p>

            <button
              type="button"
              className="rules-close-btn"
              onClick={() => setShowBotInfo(false)}
            >
              {t('bots.close') || 'Close'}
            </button>
          </div>
        </div>
      )}

      {showContactModal && (
        <div className="contact-modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Contact the Kwajongen Team</h3>
            <p style={{ marginBottom: 16, color: 'rgba(15,23,42,0.75)' }}>
              Share feedback, report issues, or ask for help. We read every message.
            </p>
            <form onSubmit={handleContactSubmit} className="contact-form">
              <label className="contact-form-group">
                <span>Your Name</span>
                <input type="text" value={contactForm.name} onChange={handleContactChange('name')} placeholder="Jef de Kwajongen" required />
              </label>
              <label className="contact-form-group">
                <span>Email</span>
                <input type="email" value={contactForm.email} onChange={handleContactChange('email')} placeholder="you@example.com" required />
              </label>
              <label className="contact-form-group">
                <span>Message</span>
                <textarea value={contactForm.message} onChange={handleContactChange('message')} rows="4" placeholder="Let us know how we can help" required />
              </label>
              {contactStatus && (
                <div className={`contact-status ${contactStatus.type === 'success' ? 'contact-status--success' : 'contact-status--error'}`}>
                  {contactStatus.text}
                </div>
              )}
              <div className="contact-form-actions">
                <button type="button" className="rules-close-btn" onClick={() => setShowContactModal(false)}>
                  Close
                </button>
                <button type="submit" className="primary-btn" disabled={isSubmittingContact}>
                  {isSubmittingContact ? 'Sending…' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Lobby;
